'use client';

import { useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import ContactSales from '@/decodo/blocks/PricingCards/ContactSalesCard';
import Features, {
  featuresMapToProductType,
  FeaturesProps,
} from '@/decodo/blocks/PricingCards/Features';
import { PaymentMethods } from '@/decodo/blocks/PricingCards/PaymentMethods';
import PricingCard, {
  PricingCardProps,
} from '@/decodo/blocks/PricingCards/PricingCard';
import {
  PricingCardContactSalesProps,
  PricingCardsFeatureListStrapi,
} from '@/decodo/blocks/PricingCards/PricingCardsStrapi';
import {
  Product,
  useProductsPricingCardsText,
} from '@/decodo/blocks/PricingCards/product';
import ImageStrapi from '@/decodo/components/atoms/Image/ImageStrapi';
import Switch from '@/decodo/components/atoms/Switch';
import { Toggle, ToggleProps } from '@/decodo/components/atoms/Toggle';

import IconArrowRight from '~/icons/arrow-right.svg';
import IconApproveCircle from '~/icons/check-circle.svg';
import IconContactSales from '~/svg/IconContactSales.svg';
import IconHandHolding from '~/svg/IconHandHolding.svg';

export type ProductPriceTypes = 'regular' | 'enterprise';
export type ProductSequantialTypes = 'sequential' | 'non-sequential';

export type PricingCardsProps = {
  cards: {
    productType?: Product;
    productPriceCategory: ProductPriceTypes;
    productSequence?: ProductSequantialTypes;
    hiddenButProductTypeShown?: boolean;
    isContactCard?: boolean;
    // card itself
    pricingCard: PricingCardProps;
  }[];
  contactSalesCards?: PricingCardContactSalesProps;
  features?: PricingCardsFeatureListStrapi['featuresList'];
  toggleVariant: ToggleProps['variant'];
};

function groupByProductCategory<T extends string>(
  cards: PricingCardsProps['cards'],
  searchByKey: 'productType' | 'productPriceCategory' | 'productSequence'
): Record<T, PricingCardsProps['cards']> {
  return cards.reduce((acc, card) => {
    if (!card || !card[searchByKey]) {
      return acc;
    }

    const key = card[searchByKey] as T;

    if (key in acc) {
      acc[key].push(card);
    } else if (card[searchByKey]) {
      acc[key] = [card];
    } else {
      // this should not happen
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acc['default'] = acc['default'] ? acc['default'].concat(card) : [card];
    }
    return acc;
  }, {} as Record<T, PricingCardsProps['cards']>);
}

export default function PricingCards({
  cards,
  features,
  contactSalesCards,
  toggleVariant,
}: PricingCardsProps) {
  const { texts } = useTranslation();
  const productsPricingCardsText = useProductsPricingCardsText();

  const priceText = {
    regular: texts.pricing_cards.regular,
    enterprise: texts.pricing_cards.enterprise,
  } as const;

  const [productSequential, setProductSequential] =
    useState<ProductSequantialTypes>('sequential');
  const [productPriceType, setProductPriceType] =
    useState<ProductPriceTypes>('regular');

  const cardsGroupedByProductType: {
    [key in Product]: PricingCardsProps['cards'];
  } = groupByProductCategory<Product>(cards, 'productType');
  const cardsGroupedByProductPriceType: {
    [key in ProductPriceTypes]: PricingCardsProps['cards'];
  } = groupByProductCategory<ProductPriceTypes>(cards, 'productPriceCategory');

  const [selectedProduct, setProduct] = useState<Product>(
    Object.keys(cardsGroupedByProductType)[0] as Product
  );

  // removing "none" category because it doesn't have any category but exists because of prismic migration, will be removed in the future
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  !!cardsGroupedByProductType?.none && delete cardsGroupedByProductType.none;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  !!cardsGroupedByProductType?.ecommerce &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    delete cardsGroupedByProductType.ecommerce;

  function checkIfOneProduct() {
    return Object.keys(cardsGroupedByProductType).length === 1;
  }

  function checkIfMultipleProducts() {
    return Object.keys(cardsGroupedByProductType).length > 1;
  }

  function checkIfHasMorePriceTypes() {
    return Object.keys(cardsGroupedByProductPriceType).length > 1;
  }

  function checkIfProductHasSequentialAndNonSequantial() {
    if (!selectedProduct) return false;

    const hasSequential = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productSequence === 'sequential'
    );
    const hasNonSequential = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productSequence === 'non-sequential'
    );

    return hasSequential && hasNonSequential;
  }

  function checkIfProductHasMorePriceCategory() {
    if (!selectedProduct) return false;

    const hasEnterprise = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productPriceCategory === 'enterprise'
    );
    const hasRegular = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productPriceCategory === 'regular'
    );

    return hasEnterprise && hasRegular;
  }

  function handleEnterpriseToggle() {
    setProductPriceType(
      productPriceType === 'regular' ? 'enterprise' : 'regular'
    );
  }

  function handleSequentialToggle() {
    setProductSequential(
      productSequential === 'sequential' ? 'non-sequential' : 'sequential'
    );
  }

  // cards
  const filteredCardsByPriceCategory = checkIfProductHasMorePriceCategory()
    ? cardsGroupedByProductType[selectedProduct].filter(
        (card) => card.productPriceCategory === productPriceType
      )
    : cardsGroupedByProductType[selectedProduct];

  const filteredCardsBySequential =
    checkIfProductHasSequentialAndNonSequantial()
      ? filteredCardsByPriceCategory.filter(
          (card) => card.productSequence === productSequential
        )
      : filteredCardsByPriceCategory;

  const shownCards =
    filteredCardsBySequential?.filter(
      (card) => !card.hiddenButProductTypeShown && !card.isContactCard
    ) || [];

  // features
  let selectedFeatures: Omit<FeaturesProps, 'inRow'> | undefined;

  if (features?.data?.length) {
    const filteredFeature = features.data.find(
      (feature) => feature.attributes.productType === selectedProduct
    );

    if (filteredFeature) {
      selectedFeatures = {
        icon: filteredFeature.attributes.icon ? (
          <ImageStrapi
            block={filteredFeature.attributes.icon}
            inner={{ classNames: { image: 'w-4 h-4' } }}
          />
        ) : undefined,
        title: filteredFeature.attributes.title,
        features:
          filteredFeature.attributes.features?.map(
            ({ name }: { name: string }) => ({
              name,
            })
          ) || [],
      };
    }
  }

  if (!selectedFeatures) {
    // Handle fallback when no matching attributes are found
    const filteredFeature =
      featuresMapToProductType?.[selectedProduct]?.(texts);
    if (filteredFeature) {
      selectedFeatures = {
        icon: <IconApproveCircle className='text-success-500 h-4 w-4' />,
        features: filteredFeature.map(({ feature }) => ({ name: feature })),
      };
    }
  }

  // rest
  const hasSelectedEnterprise = productPriceType === 'enterprise';

  // TODO: this could be refactored to simply return JSX,
  // but other bits in JSX rely on these next couple values so leaving as is for now
  const selectedControlledSalesCardToRender = contactSalesCards?.data?.find(
    ({ attributes: att }) => {
      const productMatches = att.productType === selectedProduct;

      const priceCategoryMatches =
        att.productPriceCategory === productPriceType ||
        att.productPriceCategory === 'all';

      const sequentialMatches =
        att.productSequence === productSequential ||
        att.productSequence === 'all';

      return productMatches && priceCategoryMatches && sequentialMatches;
    }
  )?.attributes;

  const hasMoneyBackGuarantee = shownCards.some(
    (card) => card.pricingCard.showMoneyBack
  );

  return (
    <div className='flex w-full flex-col items-center'>
      <div
        className={cn('flex w-full flex-col items-center justify-center gap-6')}
      >
        {checkIfMultipleProducts() && (
          <Toggle
            options={Object.keys(cardsGroupedByProductType).map(
              (product) => productsPricingCardsText[product as Product]
            )}
            selectedIndex={Object.keys(cardsGroupedByProductType).indexOf(
              selectedProduct
            )}
            onValueChange={(index) => {
              const product = Object.keys(cardsGroupedByProductType)[index];
              setProductPriceType('regular');
              setProduct(product as Product);
            }}
            variant={toggleVariant}
          />
        )}
        {checkIfOneProduct() && checkIfHasMorePriceTypes() && (
          <Toggle
            options={Object.keys(cardsGroupedByProductPriceType)
              .sort()
              .reverse()
              .map((priceType) => priceText[priceType as ProductPriceTypes])}
            selectedIndex={productPriceType === 'regular' ? 0 : 1}
            onValueChange={(index) =>
              setProductPriceType(index === 0 ? 'regular' : 'enterprise')
            }
            variant={toggleVariant}
          />
        )}

        {((checkIfProductHasMorePriceCategory() && checkIfMultipleProducts()) ||
          checkIfProductHasSequentialAndNonSequantial() ||
          hasSelectedEnterprise) && (
          <div className='flex w-full flex-wrap items-center justify-start gap-6 md:justify-center'>
            {checkIfProductHasMorePriceCategory() &&
              checkIfMultipleProducts() && (
                <Switch
                  isOn={hasSelectedEnterprise}
                  onToggle={handleEnterpriseToggle}
                  startLabel={texts.pricing_cards.enterprise}
                  ButtonProps={{
                    'data-tracker-context': 'product-multi-price-category',
                  }}
                />
              )}
            {checkIfProductHasSequentialAndNonSequantial() && (
              <Switch
                isOn={productSequential === 'non-sequential'}
                onToggle={handleSequentialToggle}
                startLabel={texts.pricing_cards.non_sequential}
                ButtonProps={{
                  'data-tracker-context': 'pricing-cards-non-sequential',
                }}
              />
            )}
          </div>
        )}
      </div>
      {hasSelectedEnterprise && (
        <div className='mt-6 flex w-full basis-full justify-start gap-3 md:mt-10 md:justify-center'>
          <IconHandHolding />{' '}
          {texts.pricing_cards.dedicated_account_manager_included}
        </div>
      )}

      <div
        className={cn(
          'mt-9 grid w-full grid-cols-1 items-end justify-center gap-4 md:mt-7 md:grid-cols-3'
        )}
      >
        {cards.map((pricing) => (
          <div
            className={cn(
              'h-max',
              shownCards?.length &&
                !shownCards.some(
                  (card) =>
                    card.pricingCard.title === pricing.pricingCard.title &&
                    card.productType === pricing.productType &&
                    card.productPriceCategory ===
                      pricing.productPriceCategory &&
                    card.productSequence === pricing.productSequence &&
                    !card.isContactCard
                ) &&
                'hide-content -h'
            )}
            key={
              pricing.pricingCard.title +
              pricing.productType +
              pricing.productPriceCategory +
              pricing.productSequence
            }
          >
            <PricingCard
              {...pricing.pricingCard}
              buttonText={
                pricing.pricingCard.buttonText ||
                texts.pricing_cards.contact_sales
              }
              url={pricing.pricingCard.url || '#contact-sales'}
              icon={
                pricing.pricingCard.url === '#contact-sales' ? (
                  <IconContactSales />
                ) : (
                  <IconArrowRight className='h-5 w-5' />
                )
              }
            />
          </div>
        ))}
        {!!selectedControlledSalesCardToRender && (
          <ContactSales
            button={{
              ...(selectedControlledSalesCardToRender.button || {}),
              link:
                // ignore configured link and force #contact-sales.
                '#contact-sales',
            }}
            heading={selectedControlledSalesCardToRender.heading}
            description={selectedControlledSalesCardToRender.description}
            outlineColor={selectedControlledSalesCardToRender.outlineColor}
          />
        )}
      </div>
      {!!selectedFeatures?.features?.length && (
        <div className='mt-9 w-full md:mt-[3.75rem]'>
          <Features {...selectedFeatures} />
        </div>
      )}

      <PaymentMethods
        product={selectedProduct}
        showMoneyBack={hasMoneyBackGuarantee}
      />
    </div>
  );
}
